/**
 * @jsxRuntime classic
 * @jsx jsx
 */

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { jsx } from '@emotion/react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from '@emotion/styled';

import type {
	Command,
	FloatingToolbarConfig,
	FloatingToolbarHandler,
} from '@atlaskit/editor-common/types';
import { FloatingToolbarButton as Button } from '@atlaskit/editor-common/ui';
import { findDomRefAtPos } from '@atlaskit/editor-prosemirror/utils';
import { DecorationSet } from '@atlaskit/editor-prosemirror/view';
import { akEditorFloatingPanelZIndex } from '@atlaskit/editor-shared-styles';
import EditorCloseIcon from '@atlaskit/icon/glyph/editor/close';
import EditorPanelIcon from '@atlaskit/icon/glyph/editor/panel';
import { token } from '@atlaskit/tokens';
import { TooltipPrimitive } from '@atlaskit/tooltip';
import { LogoSvg } from '@atlassian/generative-ai-modal/assets/Logo';
import { getExperimentCohort } from '@atlassian/generative-ai-modal/utils/experiments';

import { highlightSentence, unhighlightSentence } from '../pm-plugins/ai-spelling-grammar/commands';
import {
	insertSuggestionWithAnalytics,
	removeSuggestionWithAnalytics,
} from '../pm-plugins/ai-spelling-grammar/commands-with-analytics';
import { getPluginState as getSpellingAndGrammarPluginState } from '../pm-plugins/ai-spelling-grammar/plugin-factory';
import {
	getSelectedDiffObject,
	getSentenceFromDiffObject,
} from '../pm-plugins/ai-spelling-grammar/utils';
import { createOpenAIModalCommand } from '../pm-plugins/decoration/actions';
import { getPluginState as getDecorationPluginState } from '../pm-plugins/decoration/decoration-plugin-factory';
import { improveWriting } from '../prebuilt/config-items-range/enhance/enhance';
import { type AIGlobalOptIn } from '../types';

import { messages } from './messages';
import { separator } from './seperator';

type IconProps = { label: string; iconColor?: string };

const DislikeIcon = ({ label, iconColor }: IconProps) => (
	<EditorCloseIcon label={label} primaryColor={iconColor} />
);

const ImproveSentenceIcon = ({ label }: IconProps) => {
	return <LogoSvg alt={label} />;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const InlineDialog = styled(TooltipPrimitive)({
	backgroundColor: token('elevation.surface'),
	maxWidth: '300px',
	padding: token('space.150', '12px'),
	borderRadius: token('border.radius.100', '3px'),
	boxShadow: token(
		'elevation.shadow.overlay',
		'0px 8px 12px rgba(9, 30, 66, 0.15), 0px 0px 1px rgba(9, 30, 66, 0.31)',
	),
});

export const getFloatingToolbarConfig =
	({ aiGlobalOptIn }: { aiGlobalOptIn: AIGlobalOptIn }): FloatingToolbarHandler =>
	(state, intl) => {
		const pluginState = getSpellingAndGrammarPluginState(state) || {};
		const { isSpellingAndGrammarEnabled, selectedID } = pluginState;

		if (isSpellingAndGrammarEnabled && state.selection.from === state.selection.to) {
			const decorationPluginState = getDecorationPluginState(state);
			if (decorationPluginState.modalDecorationSet !== DecorationSet.empty) {
				return;
			}

			const selectedDiff = getSelectedDiffObject(pluginState, state.selection.to);

			if (!selectedDiff || !selectedID) {
				return;
			}
			const sentence = getSentenceFromDiffObject(pluginState, selectedDiff);
			const sentenceItems: FloatingToolbarConfig['items'] = [];

			if (sentence) {
				const openAIModalAndUnhighlightSentenceCommand: Command = (state, dispatch) =>
					createOpenAIModalCommand({
						state,
						configItem: improveWriting,
						aiGlobalOptIn,
						positions: {
							startPos: sentence.from,
							endPos: sentence.to,
						},
						lastTriggeredFrom: 'proactiveAIFloatingToolbar',
					})(state, dispatch) && // If modal opens, remove the hover sentence highlight
					unhighlightSentence()(state, dispatch);

				sentenceItems.push(separator(), {
					id: 'editor.ai.sg-improve-sentence',
					type: 'button',
					testId: 'ak-editor-ai-suggestion-toolbar-improve-sentence-button',
					icon: ImproveSentenceIcon,
					onClick: openAIModalAndUnhighlightSentenceCommand,
					onMouseEnter: highlightSentence(sentence),
					onMouseLeave: unhighlightSentence(),
					onFocus: highlightSentence(sentence),
					onBlur: unhighlightSentence(),
					title: intl.formatMessage(messages.improveSentenceToolbarButtonLabel),
					showTitle: true,
				});
			}

			const nodeType = [
				state.schema.nodes.paragraph,
				state.schema.nodes.text,
				state.schema.nodes.heading,
				state.schema.nodes.taskItem,
				state.schema.nodes.decisionItem,
			];

			const { from, to, text, originalText } = selectedDiff;

			const items: FloatingToolbarConfig['items'] = [
				{
					id: 'editor.ai.sg-suggestion',
					type: 'button',
					showTitle: true,
					testId: 'ak-editor-ai-suggestion-toolbar-suggestion-button',
					onClick: insertSuggestionWithAnalytics(from, to, selectedID),
					title: text,
					focusEditoronEnter: true,
				},
				...sentenceItems,
				separator(),
				{
					id: 'editor.ai.sg-dismiss',
					type: 'button',
					testId: 'ak-editor-ai-suggestion-toolbar-dismiss-button',
					icon: DislikeIcon,
					onClick: removeSuggestionWithAnalytics(originalText, selectedID),
					title: intl.formatMessage(messages.dismissSuggestion),
					focusEditoronEnter: true,
				},
			];

			const variation = getExperimentCohort('editor_ai_-_proactive_ai_model_variations');
			const modelName =
				variation === 'control' || variation === 'not-enrolled' ? 'default' : variation;
			items.push({
				type: 'custom',
				fallback: [],
				render: (editorView) => {
					if (!editorView) {
						return null;
					}
					return (
						<Button
							testId="ak-editor-ai-suggestion-toolbar-info-button"
							tooltipStyle={InlineDialog}
							tooltipContent={intl.formatMessage(messages.suggestionInformationTooltip, {
								backendModelName: <b>{modelName}</b>,
								feedback: (chunks: React.ReactNode[]) => (
									<a
										href="https://atlassian.enterprise.slack.com/archives/C0779029TLP"
										target="_blank"
									>
										{chunks}
									</a>
								),
							})}
							icon={<EditorPanelIcon label="info" />}
							hideTooltipOnClick={false}
						/>
					);
				},
			});

			return {
				title: 'Spelling & Grammar controls',
				getDomRef: (editorView) =>
					findDomRefAtPos(from, editorView.domAtPos.bind(editorView)) as HTMLElement,
				nodeType,
				zIndex: akEditorFloatingPanelZIndex + 1, // Place the context menu slightly above the others
				items,
				scrollable: false,
				align: 'left',
			};
		}
		return;
	};
